import { useEffect, useState } from "react";
import { Bell, Download } from "lucide-react";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { when, errText } from "@/components/admin/giftcards/shared";

const MostWanted = ({ items }) => {
  if (!items.length) return null;
  const top = items[0].count;
  return (
    <div className="rounded-xl border border-slate-100 p-3" data-testid="gc-waitlist-most-wanted">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Most wanted brands</div>
      <div className="space-y-1.5">
        {items.slice(0, 8).map((b, i) => (
          <div key={b.product_id} className="flex items-center gap-3 text-xs" data-testid={`gc-most-wanted-${b.product_id}`}>
            <span className="w-4 text-slate-400 tabular-nums">{i + 1}</span>
            <span className="w-28 truncate text-slate-900 font-medium">{b.brand}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-2 bg-cyan-600 rounded-full" style={{ width: `${(b.count / top) * 100}%` }} />
            </div>
            <span className="w-16 text-right tabular-nums text-slate-600">{b.count} {b.count === 1 ? "shopper" : "shoppers"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WaitlistPanel = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/rewards/waitlist").then((r) => setData(r.data)).catch((e) => setErr(errText(e, "Could not load the waitlist")));
  }, []);

  return (
    <div className="pf-card p-4 space-y-3" data-testid="gc-waitlist-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-900 inline-flex items-center gap-2"><Bell className="w-4 h-4 text-cyan-700" /> Gift card waitlist</h2>
          <p className="text-xs text-slate-500 mt-0.5">Shoppers who tapped "Notify me". Each gets one email and one in-app notice the first time gift cards are switched on.</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span>Total <b className="tabular-nums" data-testid="gc-waitlist-total">{data?.total ?? "·"}</b></span>
          <span>Notified <b className="tabular-nums" data-testid="gc-waitlist-notified">{data?.notified ?? "·"}</b></span>
          <button onClick={() => downloadCsv("/admin/rewards/waitlist/export.csv", "gift-card-waitlist.csv")}
                  disabled={!data?.total}
                  className="px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="gc-waitlist-export">
            <Download className="w-3.5 h-3.5" /> Download CSV
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-rose-700" data-testid="gc-waitlist-error">{err}</div>}
      <MostWanted items={data?.top_brands || []} />
      {data && !data.items.length && <div className="text-sm text-slate-500 text-center py-6" data-testid="gc-waitlist-empty">No shoppers on the waitlist yet.</div>}
      {data?.items.length > 0 && (
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="gc-waitlist-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                {["Name", "Email", "Brands wanted", "Joined", "Notified"].map((h) => <th key={h} className="py-2 pr-3 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-50" data-testid={`gc-waitlist-row-${r.user_id}`}>
                  <td className="py-2 pr-3 text-slate-900">{r.name || "—"}</td>
                  <td className="py-2 pr-3 text-slate-700">{r.email || "—"}</td>
                  <td className="py-2 pr-3 text-xs text-slate-600">{r.brands?.length ? r.brands.join(", ") : "—"}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{when(r.joined_at)}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{r.notified_at ? when(r.notified_at) : "Not yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
